import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Menu,
  MenuItem,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Card,
  IconButton,
  useTheme,
  useMediaQuery,
  Pagination,
  FormControl,
  Select,
  Stack,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import Print from "./Print";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const XrayCollectFees = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedXray, setSelectedXray] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isPaidFilter, setIsPaidFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("today");
  const open = Boolean(anchorEl);
  const [printData, setPrintData] = useState("");
  const printRef = useRef();
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalXrayPayment, setTotalXrayPayment] = useState(0);

  // Building query for backend
  const buildQuery = () => {
    let query = `patient-xray/?page=${page}&page_size=${rowsPerPage}`;
    if (searchQuery) query += `&search=${encodeURIComponent(searchQuery)}`;
    if (isPaidFilter !== "") query += `&is_paid=${isPaidFilter}`;
    if (dateFilter) query += `&date=${dateFilter}`;
    // Map frontend date_range to backend type parameter
    const typeMapping = {
      today: "daily",
      week: "weekly",
      month: "monthly",
      year: "yearly",
      custom: "",
    };
    const backendType = typeMapping[dateRangeFilter];
    if (backendType) {
      query += `&type=${backendType}`;
    }
    return query;
  };

  const { data, refetch, isLoading, isError } = useFetchObjects(
    [
      "XrayPaymentStatus",
      searchQuery,
      isPaidFilter,
      dateFilter,
      dateRangeFilter,
      page,
      rowsPerPage,
    ],
    buildQuery(),
    token
  );

  const { handleUpdate } = useUpdate("patient-xray", token);

  useEffect(() => {
    refetch();
  }, [
    searchQuery,
    isPaidFilter,
    dateFilter,
    dateRangeFilter,
    refetch,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (data && typeof data.count === "number") {
      setTotalXrayPayment(data.count);
    }
  }, [data]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleMenuClick = (event, xray) => {
    setAnchorEl(event.currentTarget);
    setSelectedXray(xray);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedXray(null);
  };

  const handleMarkAsPaid = () => {
    const formData = new FormData();
    formData.append("is_paid", true);
    formData.append("status", 2);
    handleUpdate(selectedXray.id, formData);
    handleClose();
    refetch();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => setSearchQuery("");
  const handleClearDate = () => setDateFilter("");

  // Calculate totals for X-Ray payments
  const pageTotal =
    data?.results?.reduce((sum, xray) => {
      return sum + Number(xray.total_amount || 0);
    }, 0) || 0;

  const paidTotal =
    data?.results
      ?.filter((xray) => xray.is_paid)
      .reduce((sum, xray) => sum + Number(xray.total_amount || 0), 0) || 0;

  const pendingTotal =
    data?.results
      ?.filter((xray) => !xray.is_paid)
      .reduce((sum, xray) => sum + Number(xray.total_amount || 0), 0) || 0;

  const paidCount = data?.results?.filter((xray) => xray.is_paid).length || 0;
  const pendingCount =
    data?.results?.filter((xray) => !xray.is_paid).length || 0;
  const pageCount = data?.results?.length || 0;

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      setIsPaidFilter("");
    } else if (newValue === 1) {
      setIsPaidFilter("false");
    } else if (newValue === 2) {
      setIsPaidFilter("true");
    }
  };

  const handlePrintClick = (xray) => {
    setPrintData(xray);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  return (
    <Grid container mt={4}>
      <Grid item xs={12} md={12}>
        <Card
          variant="outlined"
          sx={{
            padding: "20px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            mb: 4,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="h5"
              textAlign="center"
              fontWeight="bold"
              mb={2}
              color="primary"
            >
              {t("reception.xrayPayment")}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/reception/collect-xray-fees/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("reception.addNew")}
            </Button>
          </Box>

          {/* Enhanced Search and Filter Area */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            mb={3}
            justifyContent="space-between"
          >
            <TextField
              label={t("reception.searchPatientXray", "Search Patient X-Ray")}
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: 1,
              }}
            />
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: 180,
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <Select
                value={dateRangeFilter}
                onChange={(e) => setDateRangeFilter(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarTodayIcon color="primary" />
                  </InputAdornment>
                }
              >
                <MenuItem value="today">
                  {t("reception.today", "Today")}
                </MenuItem>
                <MenuItem value="week">
                  {t("reception.thisWeek", "This Week")}
                </MenuItem>
                <MenuItem value="month">
                  {t("reception.thisMonth", "This Month")}
                </MenuItem>
                <MenuItem value="year">
                  {t("reception.thisYear", "This Year")}
                </MenuItem>
                <MenuItem value="custom">
                  {t("reception.customRange", "Custom Range")}
                </MenuItem>
              </Select>
            </FormControl>
            {dateRangeFilter === "custom" && (
              <TextField
                type="date"
                variant="outlined"
                size="small"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                InputProps={{
                  endAdornment: dateFilter && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearDate} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 180,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
                label={t("reception.filterByDate", "Filter by Date")}
              />
            )}
          </Stack>

          {/* Status Tabs */}
          <Box display="flex" justifyContent="center" mb={2}>
            <Tabs
              value={isPaidFilter === "" ? 0 : isPaidFilter === "false" ? 1 : 2}
              onChange={handleTabChange}
              aria-label="payment status tabs"
              centered
              sx={{
                maxWidth: "500px",
                width: "100%",
                "& .MuiTabs-flexContainer": {
                  justifyContent: "space-evenly",
                },
              }}
            >
              <Tab
                label={t("reception.all")}
                sx={{
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "text.primary",
                }}
              />
              <Tab
                label={t("reception.unpaid")}
                sx={{
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "text.primary",
                }}
              />
              <Tab
                label={t("reception.paid")}
                sx={{
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "text.primary",
                }}
              />
            </Tabs>
          </Box>
        </Card>

        {/* Table Section */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box color="red" textAlign="center">
            {t("reception.errorF")}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
              borderRadius: "12px",
            }}
          >
            <Table aria-label="xray payment status table">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.date")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.patientName")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.xrayType")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.totalAmount")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.status")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "bold", backgroundColor: "grey.200" }}
                  >
                    {t("reception.action")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                {data?.results?.map((xray) => (
                  <TableRow
                    key={xray.id}
                    sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                  >
                    <TableCell align="center">{xray.date}</TableCell>
                    <TableCell align="center">{xray.patient_display}</TableCell>
                    <TableCell align="center">
                      {xray.xray_type_display}
                    </TableCell>
                    <TableCell align="center">{xray.total_amount}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          xray.is_paid
                            ? t("reception.paid")
                            : t("reception.pending")
                        }
                        color={xray.is_paid ? "success" : "warning"}
                        sx={{ fontWeight: "bold" }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {!xray.is_paid && (
                        <>
                          <Button
                            aria-controls={
                              open ? "payment-action-menu" : undefined
                            }
                            aria-haspopup="true"
                            onClick={(event) => handleMenuClick(event, xray)}
                            sx={{ minWidth: "auto", color: "#1976d2" }}
                          >
                            <MoreHorizIcon />
                          </Button>
                          <Menu
                            id="payment-action-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                          >
                            <MenuItem onClick={handleMarkAsPaid}>
                              {t("reception.markPaid")}
                            </MenuItem>
                          </Menu>
                        </>
                      )}

                      <IconButton
                        color="primary"
                        onClick={() => handlePrintClick(xray)}
                      >
                        <PrintIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      {/* Pagination Controls */}
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={4}
          mb={2}
        >
          {/* Rows Per Page Selector */}
          <Box display="flex" alignItems="center">
            <Typography variant="body2" mr={2}>
              {t("reception.rowsPerPage")}:
            </Typography>
            <FormControl variant="outlined" size="small">
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{
                  width: 80,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                {[10, 25, 50, 100, 500, 1000].map((rows) => (
                  <MenuItem key={rows} value={rows}>
                    {rows}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Pagination Component */}
          <Pagination
            count={Math.ceil(totalXrayPayment / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Grid>

      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <Print xray={printData} />
        </div>
      )}

      {/* Total Display */}
      {pageCount > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 3,
              minWidth: 200,
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              X-Ray Payments Summary:
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="success.main">
                Paid ({paidCount}):
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="success.main"
              >
                {paidTotal.toLocaleString()} ؋
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="warning.main">
                Pending ({pendingCount}):
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="warning.main"
              >
                {pendingTotal.toLocaleString()} ؋
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="primary.main">
                Total ({pageCount}):
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="primary.main"
              >
                {pageTotal.toLocaleString()} ؋
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Grid>
  );
};

export default XrayCollectFees;
